import { ChevronDown, Workflow } from '@orgus/ui/icons';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { useNavigate } from 'react-router';
import type { Id, MachineLoadState, Session } from '@orgus/protocol';
import { Button, FilterChips, Panel, ProjectMark, Pressable, Segmented, cn, formatInt, useNow } from '@orgus/ui';
import { useAllSessions, useExt, useFeed, useOrgus } from '../../lib/store.ts';
import { MACHINE_LOAD_STATE_EXT } from '../machine-load/model.ts';
import { useNeedsYouState } from '../asks/needsYou.ts';
import { useLanding } from '../landing/api.ts';
import { useMissionRows, useMissionsState } from '../missions/data.ts';
import { finishedAt } from '../missions/model.ts';
import { useProjectProtocol } from '../project-health/api.ts';
import { FLOW_STAGES, buildFlow, buildLanes, eventOf, focusInput, landingEvents, missionEvents, needsYouText, pipelineEvents, projectNameOf, resolveHit, sessionEvents } from './flow.ts';
import type { FlowEvent, FlowHit, FlowInput, FlowMission, FlowState, Lane } from './flow.ts';
import { FlowCard, FlowGraph, Ripple, STROKE, TRACE_MS, edgeAlpha } from './FlowGraph.tsx';
import { MIN_GRAPH_WIDTH, layoutFlow } from './flowLayout.ts';
import { HeroFlow } from './HeroFlow.tsx';

type FlowView = 'overall' | 'stages' | 'project';
const VIEW_KEY = 'orgus.flow.view';

/** A state as a small dot (pills and the legend). */
const DOT: Record<FlowState, string> = {
  attention: 'bg-status-attention-fill',
  danger: 'bg-status-danger-fill',
  working: 'bg-status-working-fill',
  success: 'bg-status-success-fill',
  pending: 'bg-border-strong',
  idle: 'bg-border',
};

const LEGEND: Array<{ label: string; state: FlowState }> = [
  { label: 'Needs you', state: 'attention' },
  { label: 'Working', state: 'working' },
  { label: 'Queued', state: 'pending' },
  { label: 'Landed', state: 'success' },
  { label: 'Failed', state: 'danger' },
];

/** At most this many dots in flight on the panel, and per edge; past it a new one is dropped, never queued. */
const DOTS_MAX = 8;
const DOTS_PER_EDGE = 3;
/** Events handled at once: a burst beyond this moves nothing more. */
const BATCH_MAX = 6;
/** One change often arrives twice (a feed event and the patch it came with): a node moves once in this long. */
const SAME_EVENT_MS = 800;

interface Path {
  key: string;
  d: string;
  state: FlowState;
}

interface Dot {
  id: number;
  key: string;
  d: string;
  /** The colour of the node it changes. */
  state: FlowState;
}

const reducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/** The view, remembered across visits. */
function useFlowView(): [FlowView, (v: FlowView) => void] {
  const [view, setView] = useState<FlowView>(() => {
    try {
      const v = localStorage.getItem(VIEW_KEY);
      return v === 'project' || v === 'stages' ? v : 'overall';
    } catch {
      return 'overall';
    }
  });
  return [
    view,
    (v) => {
      setView(v);
      try {
        localStorage.setItem(VIEW_KEY, v);
      } catch {
        // Storage blocked: the choice lasts for this visit.
      }
    },
  ];
}

/** An element's size, measured once on mount and again whenever it resizes. */
function useSize(ref: RefObject<HTMLElement | null>): { width: number; height: number } {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => {
      const r = el.getBoundingClientRect();
      const next = { width: Math.floor(r.width), height: Math.floor(r.height) };
      setSize((s) => (s.width === next.width && s.height === next.height ? s : next));
    };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

/** The lines between one lane's pills; under reduced motion, the ones that just carried work stay traced a moment. */
function LaneEdges({ paths, trace }: { paths: Path[]; trace: Record<string, number> }) {
  // The one clock ticks every second only while an edge is traced.
  const tracing = Object.values(trace).some((at) => at > Date.now() - TRACE_MS);
  const now = useNow(tracing ? 1000 : 60_000);
  return (
    <svg aria-hidden className="flow-lanes pointer-events-none absolute inset-0 z-(--z-base) hidden size-full overflow-visible md:block">
      {paths.map((p) => {
        const s = STROKE[p.state];
        return <path key={p.key} d={p.d} fill="none" stroke={s.color} style={{ strokeOpacity: edgeAlpha(s.opacity) }} strokeWidth={s.dashed ? 1 : 1.5} strokeDasharray={s.dashed ? '3 4' : undefined} />;
      })}
      {paths
        .filter((p) => (trace[p.key] ?? 0) > now - TRACE_MS)
        .map((p) => (
          <path key={`trace:${p.key}`} d={p.d} fill="none" stroke="var(--live-trace-strong)" strokeWidth={1.5} />
        ))}
    </svg>
  );
}

/** The lines between each lane's pills, measured: a straight join from one pill to the next. */
function useLanePaths(box: RefObject<HTMLDivElement | null>, els: RefObject<Map<string, HTMLElement>>, lanes: readonly Lane[], key: string): Path[] {
  const [paths, setPaths] = useState<Path[]>([]);
  useLayoutEffect(() => {
    const root = box.current;
    if (!root) return;
    const draw = () => {
      const r = root.getBoundingClientRect();
      const next: Path[] = [];
      for (const l of lanes)
        l.links.forEach((state, i) => {
          const from = `lane:${l.projectId}:${i}`;
          const to = `lane:${l.projectId}:${i + 1}`;
          const a = els.current.get(from)?.getBoundingClientRect();
          const b = els.current.get(to)?.getBoundingClientRect();
          if (!a || !b || a.width === 0 || b.width === 0) return;
          // Through the first line's centre (each node's value line is 20 px, h-5): the line under it is detail.
          const y = a.top + 10 - r.top;
          next.push({ key: `${from}>${to}`, d: `M ${a.right - r.left} ${y} L ${b.left - r.left} ${y}`, state });
        });
      setPaths(next);
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(root);
    return () => ro.disconnect();
  }, [key]);
  return paths;
}

const plural = (n: number, one: string, many: string) => `${formatInt(n)} ${n === 1 ? one : many}`;

const StateDot = ({ state }: { state: FlowState }) => <span aria-hidden className={cn('size-2 shrink-0 rounded-pill', DOT[state])} />;

/** A stage with nothing in it: a quiet dash, never a value-shaped placeholder. */
const Nothing = () => <span className="text-fg-subtle">-</span>;

/**
 * One project's stages, each read once, for the lanes (from md) and the list (on a phone). Each
 * has one fixed label, and its value says what it counts in words ("3 need you, 1 working").
 */
function laneStages(l: Lane): Array<{ label: string; title: string; value: ReactNode; sub?: ReactNode }> {
  const d = l.delivery;
  const inFlight = d.queued + d.verifying;
  // Parked landings and stuck failures both wait on a person (ORGU-83).
  const needs = d.parked + d.stuck;
  const deliveryWords =
    [d.queued && `${formatInt(d.queued)} queued`, d.verifying && `${formatInt(d.verifying)} verifying`, d.landed && `${formatInt(d.landed)} landed`, d.failed && `${formatInt(d.failed)} failed`]
      .filter(Boolean)
      .join(', ') || 'Nothing in delivery';
  const agentsWords =
    [l.agents.needs && needsYouText(l.agents.needs), l.agents.working && `${formatInt(l.agents.working)} working`].filter(Boolean).join(', ') ||
    (l.agents.total ? `${formatInt(l.agents.total)} idle` : 'No agents');
  const deployed = l.env.text !== 'Not deployed';
  const settled = [d.landed && `${formatInt(d.landed)} landed`, d.failed && `${formatInt(d.failed)} failed`].filter(Boolean).join(', ');
  return [
    {
      label: 'Agents',
      title: agentsWords,
      value:
        l.agents.needs || l.agents.working || l.agents.total ? (
          <>
            <StateDot state={l.agents.needs ? 'attention' : l.agents.working ? 'working' : 'idle'} />
            <span className="min-w-0 truncate num">
              {l.agents.needs > 0 && <span className="text-status-attention">{needsYouText(l.agents.needs)}</span>}
              {l.agents.needs > 0 && l.agents.working > 0 && ', '}
              {l.agents.working > 0 && <span className="text-fg-muted">{formatInt(l.agents.working)} working</span>}
              {!l.agents.needs && !l.agents.working && `${formatInt(l.agents.total)} idle`}
            </span>
          </>
        ) : (
          <Nothing />
        ),
      sub: l.agents.total ? plural(l.agents.total, 'agent', 'agents') : undefined,
    },
    {
      label: 'Delivery',
      title: deliveryWords,
      value:
        inFlight + d.landed + d.failed > 0 ? (
          <>
            <span aria-hidden className="flex h-1 w-8 shrink-0 overflow-hidden rounded-pill bg-active">
              {d.queued > 0 && <span className="bg-border-strong" style={{ flexGrow: d.queued }} />}
              {d.verifying > 0 && <span className="bg-status-working-fill" style={{ flexGrow: d.verifying }} />}
              {d.landed > 0 && <span className="bg-status-success-fill" style={{ flexGrow: d.landed }} />}
              {d.failed > 0 && <span className="bg-status-danger-fill" style={{ flexGrow: d.failed }} />}
            </span>
            <span className={cn('min-w-0 truncate num', needs ? 'text-status-attention' : 'text-fg-muted')}>
              {needs ? needsYouText(needs) : inFlight ? `${formatInt(inFlight)} in flight` : d.failed ? `${formatInt(d.failed)} failed` : `${formatInt(d.landed)} landed`}
            </span>
          </>
        ) : (
          <Nothing />
        ),
      // What already left delivery, under what is in it (said once: not when the value is it).
      sub: (d.parked || inFlight) && settled ? settled : undefined,
    },
    {
      // One label on every lane: the environment's name is the value, its state the line under it.
      label: 'Environment',
      title: deployed ? (l.env.name ? `${l.env.name}: ${l.env.text}` : l.env.text) : 'Not deployed',
      value: deployed ? (
        <>
          <StateDot state={l.env.state} />
          <span className={cn('min-w-0 truncate', l.env.name ? 'text-fg-muted' : l.env.state === 'danger' && 'text-status-danger')}>{l.env.name ?? l.env.text}</span>
        </>
      ) : (
        <Nothing />
      ),
      sub: deployed && l.env.name ? <span className={cn(l.env.state === 'danger' && 'text-status-danger')}>{l.env.text}</span> : undefined,
    },
  ];
}

/**
 * One project across the stages. From md: a slim row, each stage's value as plain caption text,
 * left-aligned in its column under the stage titles, joined by lines in the colour of what moves.
 * On a phone: the project, then its three stages stacked, each a fixed label and its value.
 */
function LaneRow({ lane: l, stamps, nodeRef, onOpen }: { lane: Lane; stamps: Record<string, number>; nodeRef: (id: string) => (el: HTMLElement | null) => void; onOpen: () => void }) {
  const id = (c: number) => `lane:${l.projectId}:${c}`;
  const stages = laneStages(l);
  return (
    <Pressable
      onClick={onOpen}
      className="relative z-(--z-base) flex min-w-0 flex-col items-start gap-2 rounded-control px-2 py-2 hover:bg-hover md:min-h-row-2line md:max-h-20 md:flex-1 md:flex-row md:items-start md:gap-3 md:py-2"
    >
      <span ref={nodeRef(id(0))} className="relative flex w-full shrink-0 items-start gap-2 md:w-48" title={l.label}>
        <span className="relative flex shrink-0">
          <ProjectMark projectId={l.projectId} name={l.label} />
          <Ripple stamp={stamps[id(0)] ?? 0} />
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="flex h-5 items-center truncate text-ui font-medium text-fg">{l.label}</span>
          {l.meta !== 'Quiet' && <span className="truncate text-caption text-fg-subtle num">{l.meta}</span>}
        </span>
      </span>
      {/* A phone: each stage a fixed label and its value, whole (the line under it wraps, never cut). */}
      <span className="flex w-full flex-col gap-1 md:hidden">
        {stages.map((s, i) => (
          <span key={s.label} title={s.title} className="relative flex min-w-0 items-baseline gap-3 rounded-control text-caption text-fg-subtle">
            <span className="w-20 shrink-0">{s.label}</span>
            <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2">
              <span className="flex min-w-0 items-center gap-1">{s.value}</span>
              {s.sub && <span className="min-w-0 text-fg-subtle num">{s.sub}</span>}
            </span>
            <Ripple stamp={stamps[id(i + 1)] ?? 0} />
          </span>
        ))}
      </span>
      {stages.map((s, i) => (
        <span key={s.label} className="hidden min-w-0 flex-1 md:flex">
          <span ref={nodeRef(id(i + 1))} title={s.title} className="relative inline-flex min-w-0 max-w-full flex-col justify-center rounded-control px-2 text-caption text-fg-subtle">
            <span className="sr-only">{s.label}</span>
            <span className="flex h-5 min-w-0 items-center gap-1">{s.value}</span>
            {s.sub && <span className="min-w-0 truncate pl-3 num">{s.sub}</span>}
            <Ripple stamp={stamps[id(i + 1)] ?? 0} />
          </span>
        </span>
      ))}
    </Pressable>
  );
}

/**
 * The whole lifecycle in one picture, read two ways. Overall: Projects, Agents, Delivery and
 * Environments as one graph on React Flow (ADR 0065), or one project when you focus it; every
 * agent joins the delivery stages its projects' changes are in. By project: a slim lane per
 * project across the same stages. Cards, pills and lines carry the colour of their state, softly.
 * When an event arrives (a session works or asks, a change is queued, verified, lands or fails, a
 * deploy goes live), one dot travels the line into its node or pill, and that one ripples: motion
 * only because something happened.
 */
export function OrchestrationFlow({ className, bare }: { className?: string; bare?: boolean }) {
  const navigate = useNavigate();
  const sessions = useAllSessions();
  const byId = useOrgus((s) => s.sessions);
  const projects = useOrgus((s) => s.projects);
  const pipelinesMap = useOrgus((s) => s.pipelines);
  const checkoutsMap = useOrgus((s) => s.checkouts);
  const { state: landing } = useLanding();
  const needs = useNeedsYouState().bySession;
  const pp = useProjectProtocol().state;
  const loadSlice = useExt<MachineLoadState>(MACHINE_LOAD_STATE_EXT);
  // The deploy targets projects report (deploys.json), the ones Environments lists.
  const deploys = useMemo(() => (pp?.projects ?? []).flatMap((r) => r.deploys.map((target) => ({ projectId: r.projectId, target }))), [pp]);
  // Missions as their live views read them (the Missions page), and the backlog items Ready to start.
  const missionsState = useMissionsState().state;
  const missionNow = useNow(60_000);
  const rows = useMissionRows(missionsState, missionNow);
  const missions = useMemo<FlowMission[]>(
    () => rows.map((r) => ({ id: r.mission.id, projectId: r.mission.projectId, state: r.view.state, finishedAt: r.view.state === 'done' || r.view.state === 'failed' ? finishedAt(r.mission) : null })),
    [rows],
  );
  const ready = useMemo(() => (missionsState?.items ?? []).filter((i) => i.status === 'ready' && i.kind !== 'epic' && i.kind !== 'feature'), [missionsState]);
  const feed = useFeed(50);
  const [view, setView] = useFlowView();
  const [focus, setFocus] = useState<Id | null>(null);
  const pipelines = useMemo(() => Object.values(pipelinesMap), [pipelinesMap]);
  const checkouts = useMemo(() => Object.values(checkoutsMap), [checkoutsMap]);
  const input = useMemo<FlowInput>(() => ({ sessions, projects, pipelines, checkouts, landing, needs, deploys, missions, ready, machineLoad: loadSlice, paused: !!missionsState?.pause }), [sessions, projects, pipelines, checkouts, landing, needs, deploys, missions, ready, loadSlice, missionsState?.pause]);
  const flow = useMemo(() => buildFlow(focus ? focusInput(input, focus) : input), [input, focus]);
  const lanes = useMemo(() => buildLanes(input), [input]);
  // Overall is the hero picture (HeroFlow); Stages is the full lifecycle graph, with missions and environments.
  const hero = view === 'overall';
  const overall = view === 'stages';

  const box = useRef<HTMLDivElement>(null);
  const { width } = useSize(box);
  const graph = overall && width >= MIN_GRAPH_WIDTH;
  // Laid out for the measured width, exactly as tall as its content.
  const layout = useMemo(() => (graph ? layoutFlow(flow, width) : null), [graph, flow, width]);

  const els = useRef(new Map<string, HTMLElement>());
  const [dots, setDots] = useState<Dot[]>([]);
  const [stamps, setStamps] = useState<Record<string, number>>({});
  const [trace, setTrace] = useState<Record<string, number>>({});

  const laneKey = view !== 'project' ? '' : `${width}#${lanes.lanes.map((l) => `${l.projectId}:${l.links.join(',')}`).join('|')}`;
  const lanePaths = useLanePaths(box, els, view !== 'project' ? [] : lanes.lanes, laneKey);
  // The lines a dot can travel: the graph's edges, or each lane's joins.
  const paths = useMemo<Path[]>(() => (overall ? (layout?.edges.map((e) => ({ key: e.id, d: e.d, state: e.state })) ?? []) : lanePaths), [overall, layout, lanePaths]);

  // Events become motion: one ripple on the node or pill, and one dot along the line into it.
  const latest = useRef({ flow, lanes, paths, overall, byId, pipelines });
  latest.current = { flow, lanes, paths, overall, byId, pipelines };
  const seq = useRef(0);
  const lastHit = useRef(new Map<string, number>());
  const stateOf = (id: string): FlowState => latest.current.flow.nodes.find((n) => n.id === id)?.state ?? 'idle';
  const fire = (events: FlowEvent[]) => {
    if (!events.length || (typeof document !== 'undefined' && document.visibilityState === 'hidden')) return;
    const cur = latest.current;
    const hits: FlowHit[] = [];
    for (const ev of events) {
      if (cur.overall) {
        const h = resolveHit(cur.flow, ev);
        if (h) hits.push(h);
      } else if (ev.projectId && cur.lanes.lanes.some((l) => l.projectId === ev.projectId)) {
        hits.push({
          node: `lane:${ev.projectId}:${ev.column}`,
          from: ev.column > 0 ? `lane:${ev.projectId}:${ev.column - 1}` : null,
        });
      }
    }
    const seen = new Set<string>();
    const at = Date.now();
    const batch = hits.filter((h) => !seen.has(h.node) && seen.add(h.node) && at - (lastHit.current.get(h.node) ?? 0) > SAME_EVENT_MS).slice(0, BATCH_MAX);
    if (!batch.length) return;
    for (const h of batch) lastHit.current.set(h.node, at);
    const withPath = batch.map((h) => (h.from ? cur.paths.find((p) => p.key === `${h.from}>${h.node}`) : undefined)).filter((p): p is Path => !!p);
    if (reducedMotion()) {
      if (withPath.length)
        setTrace((t) => ({
          ...Object.fromEntries(Object.entries(t).filter(([, x]) => x > at - TRACE_MS)),
          ...Object.fromEntries(withPath.map((p) => [p.key, at])),
        }));
      return;
    }
    setStamps((s) => {
      const next = { ...s };
      for (const h of batch) next[h.node] = (next[h.node] ?? 0) + 1;
      return next;
    });
    if (withPath.length)
      setDots((all) => {
        const out = [...all];
        for (const p of withPath) {
          if (out.length >= DOTS_MAX || out.filter((d) => d.key === p.key).length >= DOTS_PER_EDGE) continue;
          out.push({ id: ++seq.current, key: p.key, d: p.d, state: stateOf(p.key.split('>')[1] ?? '') });
        }
        return out;
      });
  };

  // The feed: each new event, from where it was last read.
  const seenFeed = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const newest = feed[0]?.id ?? null;
    if (seenFeed.current === undefined) {
      seenFeed.current = newest;
      return;
    }
    const fresh = [];
    for (const e of feed) {
      if (e.id === seenFeed.current) break;
      fresh.push(e);
    }
    seenFeed.current = newest;
    const { byId: s, pipelines: p } = latest.current;
    fire(
      fresh
        .reverse()
        .map((e) => eventOf(e, { sessions: s, pipelines: p }))
        .filter((x): x is FlowEvent => !!x),
    );
  }, [feed]);

  // Sessions that start working (SSE patches carry no feed event for that).
  const prevSessions = useRef<Record<Id, Session> | null>(null);
  useEffect(() => {
    if (prevSessions.current) fire(sessionEvents(prevSessions.current, byId));
    prevSessions.current = byId;
  }, [byId]);

  // The landing module's patches: landings that move and deployments that start, go live or fail.
  const prevLanding = useRef(landing);
  useEffect(() => {
    fire(landingEvents(prevLanding.current, landing, latest.current.byId));
    prevLanding.current = landing;
  }, [landing]);

  // Every pipeline's changes (the agent's feed only announces an item once it finishes): a change
  // queued, verifying, landed or failed sends its dot along the chain.
  const prevPipelines = useRef<typeof pipelines | null>(null);
  useEffect(() => {
    if (prevPipelines.current) fire(pipelineEvents(prevPipelines.current, pipelines, latest.current.byId));
    prevPipelines.current = pipelines;
  }, [pipelines]);

  // Missions planned, started, waiting or finished, and items made Ready.
  const prevMissions = useRef<{ missions: FlowMission[]; ready: number } | null>(null);
  useEffect(() => {
    if (!missionsState) return;
    const next = { missions, ready: ready.length };
    fire(missionEvents(prevMissions.current, next));
    prevMissions.current = next;
  }, [missions, ready, missionsState]);

  // A view switch drops the dots in flight: their lines are gone.
  useEffect(() => setDots([]), [view, focus]);

  // The one clock ticks every second only while a line is traced (reduced motion).
  const tracing = Object.values(trace).some((at) => at > Date.now() - TRACE_MS);
  const now = useNow(tracing ? 1000 : 60_000);
  const motion = useMemo(() => ({ stamps, trace, now, open: (href: string) => navigate(href) }), [stamps, trace, now, navigate]);

  const nodeRef = (id: string) => (el: HTMLElement | null) => {
    if (el) els.current.set(id, el);
    else els.current.delete(id);
  };

  // The legend names the states the picture shows, every one of them: it wraps, never cut by width.
  const present = new Set<FlowState>([...flow.nodes.map((n) => n.state), ...flow.edges.map((e) => e.state)]);
  const legend = (
    <ul className="flex flex-wrap items-center gap-x-3 gap-y-1 whitespace-nowrap" aria-label="Colours">
      {LEGEND.filter((l) => present.has(l.state)).map((l) => (
        <li key={l.label} className="flex items-center gap-1 text-caption text-fg-subtle">
          <StateDot state={l.state} />
          {l.label}
        </li>
      ))}
    </ul>
  );
  const choose = (v: FlowView) => {
    setView(v);
    if (v !== 'stages') setFocus(null);
  };
  const switcher = (
    <Segmented<FlowView>
      label="View"
      value={view}
      onChange={choose}
      options={[
        { value: 'overall', label: 'Overall' },
        { value: 'stages', label: 'Stages' },
        { value: 'project', label: 'By project' },
      ]}
    />
  );
  const focusName = focus ? projectNameOf(input, focus) : null;

  const dotLayer = dots.map((d) => (
    <span
      key={d.id}
      aria-hidden
      className="motion-flow orgus-overview-flow-dot pointer-events-none absolute left-0 top-0 z-(--z-sticky) hidden size-2 rounded-pill md:block"
      style={{ offsetPath: `path('${d.d}')`, color: d.state === 'idle' || d.state === 'pending' ? 'var(--live-trace-strong)' : STROKE[d.state].color }}
      onAnimationEnd={() => setDots((all) => all.filter((x) => x.id !== d.id))}
    />
  ));

  const actions = (
    <span className="flex min-w-0 items-center gap-3">
      {focusName ? (
        <FilterChips chips={[{ id: 'project', field: 'Project', value: focusName, onRemove: () => setFocus(null) }]} />
      ) : (
        !hero && <span className="hidden 2xl:flex">{legend}</span>
      )}
      <span className="hidden sm:flex">{switcher}</span>
    </span>
  );
  const body = (
    <div className="flex flex-1 flex-col gap-3">
      <div className="sm:hidden">{switcher}</div>
      {!hero && <div className={cn(!focusName && '2xl:hidden')}>{legend}</div>}
      <div ref={box} className="relative flex flex-1 flex-col">
        {hero ? (
          <HeroFlow />
        ) : overall && layout ? (
          <FlowGraph layout={layout} motion={motion} selected={focus ? `project:${focus}` : null}>
            {dotLayer}
          </FlowGraph>
        ) : overall ? (
          // A phone: the stages stack, each led down from the one before.
          <div className="flex flex-col gap-1 py-1">
            {FLOW_STAGES.map((stage, c) => {
              const nodes = flow.nodes.filter((n) => n.column === c);
              return (
                <section key={stage.title} aria-label={stage.title} className="flex min-w-0 flex-col gap-2">
                  {c > 0 && (
                    <span aria-hidden className="flex justify-center text-fg-subtle [&_svg]:size-4">
                      <ChevronDown />
                    </span>
                  )}
                  <h3 className="flex items-baseline gap-1 truncate pb-1 text-caption text-fg-subtle">
                    {stage.title}
                    {flow.totals[c] > 0 && <span className="num">{formatInt(flow.totals[c])}</span>}
                  </h3>
                  {nodes.length === 0 ? (
                    <span className="flex min-h-row-2line items-center rounded-control border border-dashed border-border bg-surface px-3 text-caption text-fg-subtle">{stage.empty}</span>
                  ) : (
                    // Two to a row only where a name fits whole; a phone gives each node the full width.
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {nodes.map((n) => (
                        // Up next and Missions each have a row of their own: the work reads apart from the projects.
                        <FlowCard key={n.id} n={n} stamp={stamps[n.id] ?? 0} onOpen={() => navigate(n.href)} compact className={n.kind === 'next' || n.kind === 'missions' ? 'mb-1 sm:col-span-2' : undefined} />
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        ) : lanes.lanes.length === 0 ? (
          <p className="py-8 text-center text-ui text-fg-subtle">Each project with work in progress gets a lane here, from its agents to production.</p>
        ) : (
          // The lanes share the panel's height (up to a cap each), so the row beside them never leaves a band.
          <div className="flex flex-1 flex-col gap-1 py-1">
            <div className="hidden items-center gap-3 px-2 pb-1 text-caption text-fg-subtle md:flex" aria-hidden>
              <span className="w-48 shrink-0">{FLOW_STAGES[0].title}</span>
              {FLOW_STAGES.slice(1).map((s) => (
                <span key={s.title} className="flex-1 truncate px-2">
                  {s.title}
                </span>
              ))}
            </div>
            <div className="relative flex flex-1 flex-col gap-1">
              {lanes.lanes.map((l) => (
                <LaneRow
                  key={l.projectId}
                  lane={l}
                  stamps={stamps}
                  nodeRef={nodeRef}
                  onOpen={() => {
                    setFocus(l.projectId);
                    setView('stages');
                  }}
                />
              ))}
            </div>
            {lanes.more > 0 && (
              <Button variant="ghost" size="sm" className="self-start" onClick={() => navigate('/projects')}>
                {`+${formatInt(lanes.more)} more projects`}
              </Button>
            )}
          </div>
        )}
        {view === 'project' && <LaneEdges paths={lanePaths} trace={trace} />}
        {view === 'project' && dotLayer}
      </div>
    </div>
  );
  // Bare: inside the Overview's live frame, a quiet heading row instead of a panel of its own.
  if (bare)
    return (
      <section aria-label="Flow" className={cn('flex min-w-0 flex-col gap-3', className)}>
        <div className="flex min-w-0 items-center justify-between gap-3">
          <h2 className="text-caption text-fg-subtle">{hero ? 'Projects to main' : view === 'stages' ? 'Projects to production' : 'By project'}</h2>
          {actions}
        </div>
        {body}
      </section>
    );
  return (
    <Panel icon={<Workflow />} title="Orchestration flow" className={className} bodyClassName="flex flex-col" actions={actions}>
      {body}
    </Panel>
  );
}
